import { useCallback, useEffect, useRef, useState } from "react";
import { Button, Textarea } from "@/design-system/components/ui";
import { collectionPackagesApi } from "@/api/collectionPackages";
import { extractErrorMessage } from "@/api/client";
import { SenderDetailsSection } from "./SenderDetailsSection";

// Special forms (WS-3) — the reusable merge-fields + body-edit + live-preview
// experience for generated-letter templates. Mounted by BOTH the admin
// template edit page ("template" mode, this PR) and the staff send popup
// ("send" mode, a later PR).
//
// Mode choice: "send" composes <SenderDetailsSection> whole for merge-field
// entry (its built-in preview hidden — this component owns the ONE preview so
// it can carry the body override); "template" renders only the body editor +
// preview (no sender-value entry — tokens render blank in the preview, which
// is acceptable for authoring).
//
// The component is presentational/emitter-style: the body override is fully
// CONTROLLED by the caller (`bodyOverride` + `onBodyOverrideChange`; null =
// no override, the template body renders). No React Query — the preview is a
// manual-trigger blob fetch (NEVER auto-on-edit; LibreOffice cold-starts
// serialize poorly and auto-rendering piled up into gateway 504s).

interface SpecialFormEditorProps {
  mode: "send" | "template";
  templateId: string;
  templateName: string;
  formSchemaJson: string | null;
  /** The template's canonical letter body. */
  templateBody: string;
  /** Controlled per-send (or in-progress admin-edit) body override; null =
   *  the canonical body is in effect. */
  bodyOverride: string | null;
  onBodyOverrideChange: (body: string | null) => void;
  /** Renders the body read-only (e.g. a non-holder of the special-forms
   *  admin permission viewing the template). */
  readOnlyBody?: boolean;
  // "send"-mode pass-throughs to SenderDetailsSection.
  dealId?: string | null;
  recipientName?: string | null;
  staffName?: string | null;
  staffEmail?: string | null;
  /** "send" mode — reports the captured sender values (pass-through). */
  onValuesChange?: (templateId: string, valuesJson: string) => void;
  /** "send" mode — server-resolved prefill (special-form definition
   *  endpoint) seeded as SenderDetailsSection's initial values. */
  initialValues?: Record<string, string | number | null>;
}

export function SpecialFormEditor({
  mode,
  templateId,
  templateName,
  formSchemaJson,
  templateBody,
  bodyOverride,
  onBodyOverrideChange,
  readOnlyBody = false,
  dealId = null,
  recipientName = null,
  staffName = null,
  staffEmail = null,
  onValuesChange,
  initialValues,
}: SpecialFormEditorProps) {
  const effectiveBody = bodyOverride ?? templateBody;
  const isOverridden = bodyOverride != null;

  // Sender values captured from the composed SenderDetailsSection ("send"
  // mode). "template" mode previews with no values — tokens render blank.
  const [valuesJson, setValuesJson] = useState("{}");
  const handleValuesChange = useCallback(
    (tid: string, json: string) => {
      setValuesJson(json);
      onValuesChange?.(tid, json);
    },
    [onValuesChange],
  );

  // Server-rendered PDF preview — manual-trigger only (see header comment).
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [previewStale, setPreviewStale] = useState(false);
  const urlRef = useRef<string | null>(null);

  // Flag the rendered preview as out-of-date when values or body change.
  useEffect(() => {
    if (urlRef.current) setPreviewStale(true);
  }, [valuesJson, effectiveBody]);

  // Revoke the object URL on unmount.
  useEffect(
    () => () => {
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    },
    [],
  );

  const runPreview = useCallback(async () => {
    setPreviewing(true);
    setPreviewError(null);
    try {
      const blob = await collectionPackagesApi.letterPreview(
        templateId,
        valuesJson,
        bodyOverride,
      );
      const url = URL.createObjectURL(blob);
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
      urlRef.current = url;
      setPreviewUrl(url);
      setPreviewStale(false);
    } catch (e) {
      setPreviewError(extractErrorMessage(e));
    } finally {
      setPreviewing(false);
    }
  }, [templateId, valuesJson, bodyOverride]);

  return (
    <div className="flex flex-col gap-space-4" data-testid="special-form-editor">
      {mode === "send" && (
        <SenderDetailsSection
          templateId={templateId}
          templateName={templateName}
          formSchemaJson={formSchemaJson}
          dealId={dealId}
          recipientName={recipientName}
          staffName={staffName}
          staffEmail={staffEmail}
          onValuesChange={handleValuesChange}
          hidePreview
          initialValues={initialValues}
        />
      )}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-space-4">
        <div className="flex flex-col gap-space-2">
          <div className="flex items-center justify-between gap-space-2">
            <span className="text-small font-medium text-onyx">Letter body</span>
            {isOverridden && (
              <div className="flex items-center gap-space-2">
                <span
                  className="text-caption text-gold"
                  data-testid="body-override-indicator"
                >
                  Edited from template
                </span>
                {!readOnlyBody && (
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={() => onBodyOverrideChange(null)}
                  >
                    Reset to template
                  </Button>
                )}
              </div>
            )}
          </div>
          <Textarea
            aria-label="Letter body"
            rows={18}
            value={effectiveBody}
            readOnly={readOnlyBody}
            disabled={readOnlyBody}
            onChange={(e) => onBodyOverrideChange(e.target.value)}
          />
          <p className="text-caption text-gray-mid m-0">
            {"{{Token}}"} merge fields are substituted from the sender details when
            the letter renders.
          </p>
        </div>
        <div className="flex flex-col gap-space-2">
          <div className="flex items-center justify-between gap-space-2">
            <span className="text-caption font-medium text-gray-mid">Preview</span>
            <div className="flex items-center gap-space-3">
              {previewUrl && (
                <a
                  href={previewUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-caption text-gold underline"
                >
                  Open / print
                </a>
              )}
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => void runPreview()}
                disabled={previewing}
                loading={previewing}
              >
                {previewUrl ? "Update preview" : "Generate preview"}
              </Button>
            </div>
          </div>
          {previewStale && previewUrl && !previewing && (
            <p className="text-caption text-gray-mid italic m-0">
              Changed — click "Update preview" to re-render.
            </p>
          )}
          {previewError ? (
            <p className="text-caption text-status-error m-0" role="alert">
              {previewError}
            </p>
          ) : previewUrl ? (
            <iframe
              src={previewUrl}
              title={`Preview — ${templateName}`}
              className="w-full rounded-md border border-border-light bg-white-warm"
              style={{ height: "640px" }}
            />
          ) : (
            <p className="text-caption text-gray-mid italic m-0">
              {previewing
                ? "Rendering preview…"
                : "Click “Generate preview” to render the letter."}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
